import * as amplitude from '@amplitude/analytics-browser'
import { captureException } from '@sentry/browser'
import { AxiosRequestConfig } from 'axios'
import type { UseAxiosResult } from 'axios-hooks'
import useAxios from 'axios-hooks'
import i18n from 'config/i18n'
import { addDays, subSeconds } from 'date-fns'
import { isEmpty } from 'lodash'
import type { OptionsType } from 'react-select'

import type { IParam } from 'libs/apis'

import type { IUserTechnician } from 'features/Auth'
import type {
  IFilterTicket,
  IParamTicket,
  IParamTicketAssignment,
  IProductHistory,
  IResponseProgressLogs,
  IResponseTicket,
  ISchemaFormAssignTicketPayload,
  ISchemaFormCloseTicketPayload,
  ISchemaFormPayload,
  ISchemaFormPendingTicketPayload,
  ISchemaFormPostAssignTicketPayload,
  ISchemaFormTransferTicketPayload,
  ITicketReport,
  IUpdateTaskType,
  IUser,
  TaskPDF,
  TaskReasonsResponse,
} from 'features/Ticket'
import { createEventProperties } from 'helpers/amplitudeTracker'
import { parseDate } from 'helpers/common'
import { LIMIT_PAGE } from 'helpers/constants'
import { fetchAPI } from 'libs/apis'
import type { ILoaderOption, IResponseService, Ticket } from 'types'
import { TASK_PROGRESS } from 'types'
import { TaskForApiResponse } from './TicketDownloadReport/model'

const setAssignEndDate = (date: string): Date | null => {
  const rawEndDate = parseDate(date)

  if (!rawEndDate) {
    return rawEndDate
  }

  const endDate = subSeconds(addDays(rawEndDate, 1), 1)

  return endDate
}

const nextParamsTask = (
  params?: IParamTicket,
  isDefaultStatusProgress?: boolean,
) => {
  return {
    ...params,
    _limit: LIMIT_PAGE,
    ...(params?.assignStart && { assignStart: parseDate(params.assignStart) }),
    ...(params?.assignEnd && {
      assignEnd: setAssignEndDate(params.assignEnd),
    }),
    ...(params?.['requestDate[gte]'] && {
      'requestDate[gte]': parseDate(params['requestDate[gte]']),
    }),
    ...(params?.['requestDate[lte]'] && {
      'requestDate[lte]': setAssignEndDate(params['requestDate[lte]']),
    }),
    ...(isDefaultStatusProgress && {
      statusProgress: [
        TASK_PROGRESS.NEW,
        TASK_PROGRESS.IN_PROGRESS,
        TASK_PROGRESS.PENDING,
        TASK_PROGRESS.TRANSFERRING,
        TASK_PROGRESS.REJECTED,
      ],
    }),
  }
}

export const useFindAll = (params?: IParamTicket): UseAxiosResult<Ticket[]> => {
  const path = `${window._env_.REACT_APP_API_URL}/v1/task`

  return useAxios({
    url: path,
    params: nextParamsTask(params),
  })
}

export const findAllTask = async (
  params?: IParamTicket,
  isRemoveStatusProgress?: boolean,
  options?: AxiosRequestConfig,
): Promise<IResponseService<Ticket[]>> => {
  const path = '/v1.1/task/simple'
  const withDefaultParams = {
    searchColumns: [
      'code',
      'companyName',
      'branchName',
      'branchAreaTh',
      'products.name',
    ],
    ...params,
  }
  const isDefaultStatusProgress = Boolean(
    isEmpty(params?.statusProgress) && !Boolean(isRemoveStatusProgress),
  )

  if (Boolean(isRemoveStatusProgress)) {
    delete params?.statusProgress
  }

  try {
    const response = await fetchAPI<Ticket[]>({
      apiUrl: window._env_.REACT_APP_API_URL,
      method: 'GET',
      path,
      params: nextParamsTask(withDefaultParams, isDefaultStatusProgress),
      options,
    })

    return { data: response.body, count: +response.headers['x-total-count'] }
  } catch (error) {
    captureException(error)

    return { error }
  }
}

export const useFindAllTaskType = (): UseAxiosResult<IFilterTicket[]> => {
  const path = `${window._env_.REACT_APP_API_URL}/v1/task/type`

  return useAxios({
    url: path,
  })
}

export const useFindAllTaskIssue = (): UseAxiosResult<IFilterTicket[]> => {
  const path = `${window._env_.REACT_APP_API_URL}/v1/task/issue?isActive=true`
  return useAxios({
    url: path,
  })
}

export const findAllTaskPendingReasons = async (): Promise<
  IResponseService<TaskReasonsResponse[]>
> => {
  try {
    const response = await fetchAPI<TaskReasonsResponse[]>({
      apiUrl: window._env_.REACT_APP_API_URL,
      method: 'GET',
      path: '/v1/task/pending-reasons',
    })

    return { data: response.body }
  } catch (error) {
    captureException(error)

    return { error }
  }
}

export const findAllTaskTransferReasons = async (): Promise<
  IResponseService<TaskReasonsResponse[]>
> => {
  try {
    const response = await fetchAPI<TaskReasonsResponse[]>({
      apiUrl: window._env_.REACT_APP_API_URL,
      method: 'GET',
      path: '/v1/task/task-progress-reasons',
      params: {
        _limit: 0,
        statusProgress: 'Transferring',
      },
    })

    return { data: response.body }
  } catch (error) {
    captureException(error)

    return { error }
  }
}

export const create = async (payload: ISchemaFormPayload): Promise<void> => {
  try {
    const response = await fetchAPI({
      apiUrl: window._env_.REACT_APP_API_URL,
      method: 'POST',
      path: '/v1/task',
      options: { data: payload },
    })

    if (response.status !== 201) {
      throw new Error(i18n.t('error:notFound.title'))
    }
  } catch (error) {
    captureException(error)
  }
}

export const updateTask = async (
  id: string,
  payload: IUpdateTaskType,
): Promise<void> => {
  const response = await fetchAPI({
    apiUrl: window._env_.REACT_APP_API_URL,
    method: 'PATCH',
    path: `/v2/task/${id}`,
    options: { data: payload },
  })

  if (response.status !== 200) {
    throw new Error(i18n.t('error:internalServerError.title'))
  }
}

export const findTicketById = async (
  id: string,
): Promise<IResponseService<IResponseTicket>> => {
  const withDefaultParams: IParam = {
    _order: 'ASC',
    _sort: 'firstName',
  }

  try {
    const response = await fetchAPI<IResponseTicket, IParam>({
      apiUrl: window._env_.REACT_APP_API_URL,
      method: 'GET',
      path: `/v2/task/${id}`,
      params: withDefaultParams,
    })

    return { data: response.body }
  } catch (error) {
    captureException(error)

    return { error }
  }
}

interface IResponseProduct {
  id: string
  isActive: boolean
}

export const findProductByBranchId = async <T = IResponseProduct[]>(
  branchId: string,
  q?: string,
): Promise<IResponseService<T>> => {
  try {
    const response = await fetchAPI<T>({
      apiUrl: window._env_.REACT_APP_API_URL,
      method: 'GET',
      path: `/v1/product`,
      params: { branchId, ...(q ? { q } : {}), _limit: 0 },
    })

    return { data: response.body }
  } catch (error) {
    captureException(error)

    return { error }
  }
}

export const findProgressLogs = async (
  ticketId: string,
): Promise<IResponseService<IResponseProgressLogs[]>> => {
  try {
    const response = await fetchAPI<IResponseProgressLogs[]>({
      apiUrl: window._env_.REACT_APP_API_URL,
      method: 'GET',
      path: `/v1/task/${ticketId}/progress-logs`,
      params: {
        _limit: 0,
      },
    })

    return { data: response.body, count: +response.headers['x-total-count'] }
  } catch (error) {
    captureException(error)

    return { error }
  }
}

export const useFindAllTicketDetail = (
  id: string,
): UseAxiosResult<IResponseTicket> => {
  const path = `${window._env_.REACT_APP_API_URL}/v1/task/${id}`

  return useAxios({
    url: path,
  })
}

export const useFindAllProductHistoryByProductId = (
  productId: string | number,
  params?: IParamTicket,
): UseAxiosResult<IProductHistory[]> => {
  const path = `${window._env_.REACT_APP_API_URL}/v1/product/${productId}/history`
  const nextParams = {
    ...params,
    _limit: 0,
    isCompleted: true,
  }

  return useAxios({
    url: path,
    params: nextParams,
  })
}

export const closeTicket = async (
  id: string,
  payload: ISchemaFormCloseTicketPayload,
  handleOpenSuccess: () => void,
): Promise<void> => {
  try {
    const response = await fetchAPI({
      apiUrl: window._env_.REACT_APP_API_URL,
      method: 'PATCH',
      path: `/v1/task/close/${id}`,
      options: { data: payload },
    })

    const isSuccess = response.status === 200

    if (isSuccess) {
      handleOpenSuccess()
    } else {
      throw new Error(i18n.t('error:notFound.title'))
    }
  } catch (error) {
    captureException(error)
  }
}

export const pendingTicket = async (
  id: string,
  payload: ISchemaFormPendingTicketPayload,
  handleOpenSuccess: () => void,
): Promise<void> => {
  try {
    const response = await fetchAPI({
      apiUrl: window._env_.REACT_APP_API_URL,
      method: 'PATCH',
      path: `/v1/task/pending/${id}`,
      options: { data: payload },
    })
    const isSuccess = response.status === 200

    if (isSuccess) {
      handleOpenSuccess()
    } else {
      throw new Error(i18n.t('notFound:notFound.error'))
    }
  } catch (error) {
    captureException(error)
  }
}

export const uploadPhotoBeforeTask = async (
  taskAssignId: string,
  photo: File[],
): Promise<void> => {
  try {
    const formData = new FormData()
    photo.forEach((file) => {
      formData.append('photos', file)
    })

    const response = await fetchAPI({
      apiUrl: window._env_.REACT_APP_API_URL,
      method: 'POST',
      path: `/v1/task/task-assign/${taskAssignId}/beforePhotos`,
      options: {
        data: formData,
      },
    })
    const isSuccess = response.status === 201
    if (!isSuccess) {
      throw new Error(i18n.t('error:notFound.title'))
    }
  } catch (error) {
    captureException(error)
  }
}

export const uploadPhotoAfterTask = async (
  taskAssignId: string,
  photo: File[],
): Promise<void> => {
  try {
    const formData = new FormData()
    photo.forEach((file) => {
      formData.append('photos', file)
    })

    const response = await fetchAPI({
      apiUrl: window._env_.REACT_APP_API_URL,
      method: 'POST',
      path: `/v1/task/task-assign/${taskAssignId}/afterPhotos`,
      options: {
        data: formData,
      },
    })
    const isSuccess = response.status === 201
    if (!isSuccess) {
      throw new Error(i18n.t('error:notFound.title'))
    }
  } catch (error) {
    captureException(error)
  }
}

export const assignTicket = async (
  id: string,
  payload: ISchemaFormAssignTicketPayload,
): Promise<IResponseService<IResponseTicket>> => {
  try {
    const response = await fetchAPI<IResponseTicket>({
      apiUrl: window._env_.REACT_APP_API_URL,
      method: 'PATCH',
      path: `/v1/task/assign/${id}`,
      options: { data: payload },
    })

    if (response.status !== 200) {
      throw new Error(i18n.t('error:notFound.title'))
    }

    return { data: response.body }
  } catch (error) {
    return { error }
  }
}

export const postAssignTicket = async (
  payload: ISchemaFormPostAssignTicketPayload,
  handleOpenSuccess: () => void,
): Promise<void> => {
  try {
    const response = await fetchAPI<IResponseTicket>({
      apiUrl: window._env_.REACT_APP_API_URL,
      method: 'POST',
      path: `/v1/task/task-assign`,
      options: { data: payload },
    })

    const isSuccess = response.status === 201

    if (isSuccess) {
      const eventProperties = {
        ...createEventProperties(),
        technicians: payload.users,
        taskId: payload.taskId,
        assignStart: payload.assignStart,
        assignEnd: payload.assignEnd,
        statusPriority: payload.statusPriority,
        issueDetail: payload.issueDetail,
      }

      amplitude.track('FixTab Backoffice: Assign Ticket', eventProperties)
      handleOpenSuccess()
    } else {
      throw new Error(i18n.t('error:notFound.title'))
    }
  } catch (error) {
    captureException(error)
  }
}

export const transferTicket = async (
  payload: ISchemaFormTransferTicketPayload,
  handleOpenSuccess: () => void,
): Promise<void> => {
  try {
    const response = await fetchAPI<IResponseTicket>({
      apiUrl: window._env_.REACT_APP_API_URL,
      method: 'POST',
      path: '/v2/task/transferring',
      options: {
        data: payload,
      },
    })

    const isSuccess = response.status === 201

    if (isSuccess) {
      const eventProperties = {
        ...createEventProperties(),
        technicians: payload.users,
        taskId: payload.taskId,
        transferringById: payload.transferringById,
        transferringByUser: payload.transferringByUser,
        transferringReasons: payload.transferringReasons,
        transferringDetail: payload.transferringDetail,
        statusPriority: payload.statusPriority,
        issueDetail: payload.issueDetail,
        transferringAt: payload.transferringAt,
        assignStart: payload.assignStart,
        assignEnd: payload.assignEnd,
      }

      amplitude.track('FixTab Backoffice: Transfer Ticket', eventProperties)
      handleOpenSuccess()
    } else {
      throw new Error(i18n.t('error:notFound.title'))
    }
  } catch (error) {
    captureException(error)
  }
}

export const getUsers = (q: string): Promise<OptionsType<ILoaderOption>> => {
  return new Promise(async (resolve, reject) => {
    const withDefaultParams: IParam | IUserTechnician = {
      q,
      _limit: 0,
      position: 'Technician',
      _order: 'ASC',
      _sort: 'firstName',
    }

    if (!q) {
      delete withDefaultParams.q
    }

    try {
      const response = await fetchAPI<IUser[], IParam | IUserTechnician>({
        apiUrl: window._env_.REACT_APP_API_URL,
        method: 'GET',
        path: '/v1/user',
        params: withDefaultParams,
      })

      const mapper: OptionsType<ILoaderOption> = response.body.map(
        (item: IUser) => {
          const label: string = item.lastName
            ? `${item.firstName} ${item.lastName}`
            : item.firstName

          return {
            key: item.id,
            label: label,
            value: item.id,
          }
        },
      )

      resolve(mapper)
    } catch (e) {
      reject(e)
    }
  })
}

export const useFindAllUserTasks = (
  params?: IParamTicketAssignment,
): UseAxiosResult<IUser[]> => {
  const path = `${window._env_.REACT_APP_API_MOCK_URL}/user-tasks`
  const nextParams = {
    ...params,
    ...(params?.date && { date: parseDate(params.date) }),
  }

  return useAxios({
    url: path,
    params: nextParams,
  })
}

export const useFindAllTasksReport = (): UseAxiosResult<ITicketReport[]> => {
  const path = `${window._env_.REACT_APP_API_URL}/v1/task/report`
  const nextParams = {
    _limit: 0,
  }

  return useAxios({
    url: path,
    params: nextParams,
  })
}

export const downloadTaskReport = async (
  options?: AxiosRequestConfig,
  params?: IParamTicket,
): Promise<IResponseService<TaskForApiResponse[]>> => {
  try {
    const response = await fetchAPI<TaskForApiResponse[]>({
      apiUrl: window._env_.REACT_APP_API_URL,
      method: 'GET',
      path: '/v1/task/report',
      params: {
        _limit: 0,
        ...params,
      },
      options,
      timeout: 600000, // TODO: change later when CR requirement
    })

    return { data: response.body, count: +response.headers['x-total-count'] }
  } catch (error) {
    captureException(error)

    return { error }
  }
}

export const downloadTaskDetailPDF = async (
  id: string,
  options?: AxiosRequestConfig,
): Promise<IResponseService<TaskPDF>> => {
  try {
    const response = await fetchAPI<TaskPDF>({
      apiUrl: window._env_.REACT_APP_API_URL,
      method: 'GET',
      path: '/v2/report/task',
      params: {
        taskId: id,
        language: i18n.language === 'th' ? 'th' : 'en',
      },
      options,
      timeout: 120000,
    })

    return { data: response.body, count: +response.headers['x-total-count'] }
  } catch (error) {
    captureException(error)

    throw error
  }
}
